import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { urlEvento } from '@/pages/assistente-evento/utils';

interface Props {
  eventoId: number;
  slug: string;
  nome: string;
  whatsappInicial: string;
  comoParticiparInicial: string;
  sugerirTextoInicial: string;
  aoFechar: () => void;
  aoSalvo: () => void;
}

const inputCls =
  'mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

export default function DivulgacaoEditor({
  eventoId,
  slug,
  nome,
  whatsappInicial,
  comoParticiparInicial,
  sugerirTextoInicial,
  aoFechar,
  aoSalvo,
}: Props) {
  const [whatsapp, setWhatsapp] = useState(whatsappInicial);
  const [comoParticipar, setComoParticipar] = useState(comoParticiparInicial);
  const [sugerirTexto, setSugerirTexto] = useState(sugerirTextoInicial);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  const link = slug ? urlEvento(slug) : '';

  async function copiarLink() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      /* silencioso */
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  function enviarWhatsApp() {
    if (!link) return;
    const texto = encodeURIComponent(`${nome} — veja mais e participe: ${link}`);
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener,noreferrer');
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const { error } = await supabase
        .from('eventos')
        .update({
          whatsapp_vendas: whatsapp.trim() || null,
          como_participar: comoParticipar.trim() || null,
          sugerir_texto: sugerirTexto.trim() || null,
        })
        .eq('id', eventoId);
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-5">
      {erro && (
        <p className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {erro}
        </p>
      )}

      <div className="rounded-lg border border-secondary-200 p-3">
        <span className="text-sm font-medium text-foreground-800">Link do evento</span>
        <div className="mt-2 flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md bg-background-100 px-2 py-1.5 font-mono text-xs text-foreground-800">
            {link.replace(/^https?:\/\//, '') || 'sem link'}
          </code>
          <button
            type="button"
            onClick={copiarLink}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            {copiado ? 'Copiado ✓' : 'Copiar'}
          </button>
          <button
            type="button"
            onClick={enviarWhatsApp}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-4 w-4 items-center justify-center text-green-600">
              <i className="ri-whatsapp-line" />
            </span>
            Enviar
          </button>
        </div>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">WhatsApp de vendas</span>
        <p className="mt-0.5 text-xs text-secondary-500">
          Aparece no botão de contato da página pública.
        </p>
        <input
          type="text"
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          inputMode="tel"
          className={inputCls}
          placeholder="14997889732"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Como participar</span>
        <textarea
          value={comoParticipar}
          onChange={(e) => setComoParticipar(e.target.value.slice(0, 500))}
          rows={3}
          className={`${inputCls} resize-none`}
          placeholder="Compareça no dia, a entrada é gratuita."
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Veja também</span>
        <p className="mt-0.5 text-xs text-secondary-500">
          Uma chamada curta para outra página ou evento relacionado.
        </p>
        <input
          type="text"
          value={sugerirTexto}
          onChange={(e) => setSugerirTexto(e.target.value)}
          className={inputCls}
          placeholder="Conheça também o Acampamento Juvenil"
        />
      </label>

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={aoFechar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}